import { StopOutlined } from "@ant-design/icons";
import { Button, Collapse, Tag } from "antd";
import React, { useState } from "react";
import { AddButton } from "~/components/buttons/AddButton.jsx";
import { DataCard } from "~/components/dataCards/dataCard.jsx";
import { DateFormat } from "~/components/date/DateFormat.jsx";
import { config } from "~/config.js";
import { $t } from "~/i18n.js";
import { CardGuestClubAddModal } from "./cardGuestClubAddModal.jsx";
import { CardGuestClubRemoveModal } from "./cardGuestClubRemoveModal.jsx";

const ClubTitle = ({ club, isGuestClub, isIngelogdOp }) => {
  return (
    <div className="row">
      <p>{club.full_name}</p>

      {isGuestClub && (
        <Tag color="default" style={{ marginLeft: 6 }}>
          {$t("Gastclub")}
        </Tag>
      )}

      {!isGuestClub && (
        <Tag color="default" style={{ marginLeft: 6 }}>
          {$t("Thuisclub")}
        </Tag>
      )}

      {!club.is_active && (
        <Tag color="red" style={{ marginLeft: 6 }}>
          {$t("deactief")}
        </Tag>
      )}

      <div style={{ flexGrow: 1 }} />

      {isIngelogdOp && <Tag color="green">{$t("Ingelogd op")}</Tag>}
    </div>
  );
};

const ClubRender = ({
  guestClub,
  setRemoveClub,
  isGuestClub = false,
  isIngelogdOp = false,
}) => {
  return (
    <div>
      {!isIngelogdOp && (
        <p>
          <span className="gray">{$t("Login via")}</span>:{" "}
          <a
            href={`https://${guestClub.club}.${config.frontend_base_url}`}
            target="_blank"
            style={{ marginLeft: 6 }}
          >
            <span className="bold">{guestClub.club}</span>
            <span>.{config.frontend_base_url}</span>
          </a>
        </p>
      )}

      {guestClub.date_activated && (
        <p>
          <span className="gray">{$t("Geactiveerd op")}</span>:{" "}
          <DateFormat date={guestClub.date_activated} />
        </p>
      )}

      {guestClub.date_deactivated && (
        <p>
          <span className="gray">{$t("Deactief op")}</span>:{" "}
          <DateFormat date={guestClub.date_deactivated} />
        </p>
      )}
      {setRemoveClub && guestClub.is_active && (
        <Button
          style={{ marginTop: 6 }}
          icon={<StopOutlined />}
          size="small"
          danger
          onClick={() => setRemoveClub(guestClub)}
        >
          {$t("Deactiveer")}
        </Button>
      )}

      {isGuestClub && (
        <p style={{ marginTop: 6 }}>
          {$t(
            "Je vluchten worden elke nacht gesyncroniseerd naar het logboek van je thuisclub. Instructeurs en beheerders van gastclubs hebben inzage in je profiel en kunnen je vluchten bekijken om je vliegbekwaamheid te beoordelen"
          )}
        </p>
      )}

      {isIngelogdOp && !isGuestClub && (
        <p style={{ marginTop: 6 }}>
          {$t("Dit is je thuisclub")}.
          {$t("Wijzigt dit of wil je je uitschrijven, mail het bestuur.")}
        </p>
      )}
    </div>
  );
};

export const CardGuestClubs = ({ isGastvlieger, profile, club }) => {
  const [addNew, setAddNew] = useState(false);
  const [removeClub, setRemoveClub] = useState(null);

  // Prepare club items for rendering in Collapse component
  const createClubItem = ({
    club,
    isGuestClub = false,
    isIngelogdOp = false,
    canRemove = false,
  }) => ({
    key: club.id || Math.random(),
    label: (
      <ClubTitle
        club={club}
        isGuestClub={isGuestClub}
        isIngelogdOp={isIngelogdOp}
      />
    ),
    children: (
      <ClubRender
        guestClub={
          isIngelogdOp
            ? { ...club, club: club.schema_name, is_active: true }
            : club
        }
        setRemoveClub={canRemove ? setRemoveClub : null}
        isGuestClub={isGuestClub}
        isIngelogdOp={isIngelogdOp}
      />
    ),
  });

  // Initialize items array
  let clubItems = [];
  const currentClub = { ...club, is_active: true };

  if (!isGastvlieger) {
    // Add current club
    clubItems.push(
      createClubItem({
        club: currentClub,
        isGuestClub: false,
        isIngelogdOp: true,
      })
    );
  }

  // Add home club for guest flyers
  if (isGastvlieger) {
    if (profile.home_club) {
      clubItems.push(
        createClubItem({
          club: profile.home_club,
          isGuestClub: false,
        })
      );
    }
    clubItems.push(
      createClubItem({
        club: currentClub,
        isIngelogdOp: true,
        isGuestClub: true,
      })
    );
  }

  // Add guest clubs
  const guestClubItems =
    profile?.guest_clubs?.map((guestClub) =>
      createClubItem({
        club: guestClub,
        isGuestClub: true,
        canRemove: !isGastvlieger,
      })
    ) || [];

  clubItems = [...clubItems, ...guestClubItems];

  return (
    <DataCard
      title={$t("Club lidmaatschappen")}
      onSave={null}
      extra={
        !isGastvlieger && (
          <AddButton size="small" onClick={() => setAddNew(true)} />
        )
      }
    >
      {isGastvlieger && !profile.home_club && (
        <p>
          {$t(
            "Indien je thuisclub ook de ZweefApp gebruikt, voeg deze club toe aan je profiel en je vluchten worden dagelijks gesyncroniseerd"
          )}
          .
        </p>
      )}

      {clubItems && <Collapse size="small" items={clubItems}></Collapse>}

      {!isGastvlieger &&
        (!profile.guest_clubs || profile.guest_clubs.length === 0) && (
          <p style={{ marginTop: 6 }}>
            {$t(
              "Je hebt geen gast clubs toegevoegd, indien je elders vliegt voeg deze club toe als gastclub en je vluchten worden elke nacht gesyncroniseerd"
            )}
            .
          </p>
        )}

      {addNew && (
        <CardGuestClubAddModal
          visible={addNew}
          onClose={() => setAddNew(false)}
          guest_clubs={profile.guest_clubs}
          club={club}
        />
      )}

      {removeClub && (
        <CardGuestClubRemoveModal
          guest_club={removeClub}
          onClose={() => setRemoveClub(null)}
        />
      )}
    </DataCard>
  );
};
